import { Cost, CostBuilder, CostEffect } from "./util/cost"
import { JsonHelper, type JsonMap, type JsonValue } from "./util/json"
import { isZero } from "./util/number"

export interface CapitalRequirementsPositionEntry {
  readonly instrumentSymbol: string | null
  readonly instrumentType: string | null
}

export interface CapitalRequirementsGroup {
  readonly description: string
  readonly underlyingSymbol: string | null
  readonly underlyingType: string | null
  readonly buyingPower: Cost | null
  readonly positionEntries: readonly CapitalRequirementsPositionEntry[]
}

export interface CapitalRequirements {
  readonly groups: readonly CapitalRequirementsGroup[]
  readonly totalBuyingPower: Cost
}

const JAVA_DOUBLE_GRAMMAR =
  /^\s*[+-]?(NaN|Infinity|((\d+\.?\d*)|(\.\d+))([eE][+-]?\d+)?[dDfF]?)\s*$/

function presentWireValue(helper: JsonHelper, key: string): JsonValue {
  const value = (helper.toJson() as JsonMap)[key]
  return value === undefined || value === "" ? null : value
}

function optionalString(helper: JsonHelper, key: string): string | null {
  const value = helper.getString(key)
  return value === "" ? null : value
}

function numberOrNaN(value: JsonValue): number {
  if (typeof value === "number") {
    return value
  }

  return typeof value === "string" && JAVA_DOUBLE_GRAMMAR.test(value)
    ? Number.parseFloat(value)
    : NaN
}

function toCostEffect(key: string, effect: JsonValue): CostEffect {
  switch (effect) {
    case "Debit":
      return CostEffect.Debit
    case "Credit":
      return CostEffect.Credit
    case "None":
      return CostEffect.NoCost
    default:
      throw new Error(`Unknown ${key}: ${JSON.stringify(effect)}`)
  }
}

function optionalCost(helper: JsonHelper, key: string): Cost | null {
  const effectKey = `${key}-effect`
  const wireValue = presentWireValue(helper, key)
  const wireEffect = presentWireValue(helper, effectKey)
  if (wireValue === null || wireEffect === null) {
    return null
  }

  const value = numberOrNaN(wireValue)
  const effect = toCostEffect(effectKey, wireEffect)
  if (isZero(value)) {
    return Cost.ZERO
  }

  if (!Number.isFinite(value) || effect === CostEffect.NoCost) {
    throw new Error(
      `Invalid ${key}: ${JSON.stringify(wireValue)} ${JSON.stringify(wireEffect)}`
    )
  }

  return value < 0
    ? Cost.createOpposite(-value, effect)
    : Cost.create(value, effect)
}

function parsePositionEntry(
  helper: JsonHelper
): CapitalRequirementsPositionEntry {
  return {
    instrumentSymbol: optionalString(helper, "instrument-symbol"),
    instrumentType: optionalString(helper, "instrument-type"),
  }
}

function parseDescribedGroups(helper: JsonHelper): CapitalRequirementsGroup[] {
  return helper.getChildren("groups").flatMap((child) => {
    const description = optionalString(child, "description")
    return description === null
      ? []
      : [
          {
            description,
            underlyingSymbol: optionalString(child, "underlying-symbol"),
            underlyingType: optionalString(child, "underlying-type"),
            buyingPower: optionalCost(child, "buying-power"),
            positionEntries: child
              .getChildren("position-entries")
              .map(parsePositionEntry),
          },
        ]
  })
}

function netBuyingPower(groups: readonly CapitalRequirementsGroup[]): Cost {
  return groups.reduce(
    (total, { buyingPower }) =>
      buyingPower === null ? total : total.adjust(buyingPower),
    new CostBuilder()
  ).toCost
}

export function parseCapitalRequirements(
  report: JsonHelper
): CapitalRequirements {
  const groups = parseDescribedGroups(report)

  return { groups, totalBuyingPower: netBuyingPower(groups) }
}
